/*
 * Copyright (c) Nexign, JSC, 2021-2023.
 */
/**
 *
 *
 * @author Yakov Gushchin
 * @author Ilya Babanov
 * @version 1.23.0
 * @since 1.14.0
 */

import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';

import { Input } from '@components/common';
import ArrowTooltip from '@components/common/Tooltip/Tooltip';
import FormField from '@modules/form/components/FormField';
import FormAddressOpenIcon from '@modules/formAddress/components/FormAddressOpenIcon';
import { formAddressInitializeAction } from '@modules/formAddress/formAddressActions';
import type { TPartialFormAddressConfiguration } from '@modules/formAddress/types/formAddressActionTypes';
import type { IFormAddressConfiguration } from '@modules/formAddress/types/formAddressTypes';
import { EFormAddressType } from '@modules/formAddress/types/formAddressTypes';
import { useSubscriberIdContext } from '@pages/PageSubscriberId/context';

import {
  personalDataDocumentAttributesSelector,
  personalDataMandatoryAttributesSelector,
  personalDataTranslationSelector,
} from '../../selectors';
import { PersonalDataType as T } from '../../types/meta';

interface IPersonalDataEditAddress {
  initialValue: IFormAddressConfiguration;
}

const PersonalDataEditAddress = ({
  initialValue,
}: IPersonalDataEditAddress) => {
  const { subscriberId } = useSubscriberIdContext();

  const dispatch = useDispatch();

  const { setFieldValue } = useFormikContext();

  const { address: tAddress } = useSelector(personalDataTranslationSelector());

  const documentAttributes = useSelector(
    personalDataDocumentAttributesSelector(subscriberId),
  );
  const mandatoryAttributes = useSelector(
    personalDataMandatoryAttributesSelector(subscriberId),
  );

  const initializeFormAddress = () => {
    dispatch(formAddressInitializeAction({ address: initialValue }));
  };

  const onSubmit = (address: TPartialFormAddressConfiguration) => {
    setFieldValue(T.address, address);
  };

  if (!documentAttributes.includes(T.address)) return null;

  return (
    <div className="edit-form__address-filed">
      <FormField
        dataName="edit-address"
        errorKey={`${T.address}.addressString`}
        label={tAddress.title}
        name={T.address}
        required={mandatoryAttributes.includes(T.address)}
      >
        {({ field, kind, label, dataName, required }) => (
          <ArrowTooltip title={field.value.addressString}>
            <div>
              <Input
                {...field}
                dataName={dataName}
                disabled={true}
                kind={kind}
                label={label}
                required={required}
                value={field.value.addressString}
              />
            </div>
          </ArrowTooltip>
        )}
      </FormField>
      <FormAddressOpenIcon
        initialize={initializeFormAddress}
        isDisabled={false}
        type={EFormAddressType.subscriber}
        onSubmit={onSubmit}
      />
    </div>
  );
};

export default PersonalDataEditAddress;
